import { WatchQueryFetchPolicy } from "@apollo/client";

let apiHookCalledMap: Record<string, Date> = {};

interface FetchPolicyOptions {
  fetchPolicy: WatchQueryFetchPolicy;
  onCompleted: (data: unknown) => void;
}

const TIME_TO_STALE = 3600 * 1000; // 1h in ms

const isStale = (hookKey: string) => {
  if (!apiHookCalledMap[hookKey]) {
    return true;
  }
  return (
    new Date().getTime() - apiHookCalledMap[hookKey].getTime() > TIME_TO_STALE
  );
};

/*
App persists its Apollo Client cache to localStorage
To have fresh data we get the data from api on every first hook call after app refresh
apiHookCalledMap keeping information when specified hook was rendered in current run
if hook was rendered less than TIME_TO_STALE we set fetchPolicy to "cache-only" if not we set "cache-and-network" (displays cached data but fetch current from api)
Apollo Client provides nextFetchPolicy, but it is reset to fetchPolicy after render of component where hook was used
 */
export const getFetchPolicyOptions = (hookKey: string): FetchPolicyOptions => ({
  fetchPolicy:
    isStale(hookKey) && window.navigator.onLine
      ? "cache-and-network"
      : "cache-only",
  onCompleted: () => {
    markFetchPolicyKeyFresh(hookKey);
  },
});

/**
 * Stamps a fetch-policy key as freshly fetched so subsequent
 * getFetchPolicyOptions(key) calls within TIME_TO_STALE resolve to "cache-only".
 * Used by the login-time prefetch so visiting a page does not re-fetch what was
 * just warmed.
 */
export const markFetchPolicyKeyFresh = (hookKey: string) => {
  apiHookCalledMap = {
    ...apiHookCalledMap,
    [hookKey]: new Date(),
  };
};

/**
 * Builds the cache/staleness key for the positions-and-figures analytics query.
 * MUST include the portfolio id: the analytics calc is per-portfolio, so a
 * contact-only key makes every portfolio of a multi-portfolio client share one
 * freshness stamp — warming one portfolio would wrongly mark the others fresh
 * and starve them (they'd resolve to "cache-only" against an empty cache).
 */
export const getPositionsAndFiguresCacheKey = (
  contactId: number | string | undefined | null,
  portfolioId: number | undefined
) => `cacheKeyForPositionsAndFigures_${contactId}_${portfolioId}`;
